/**
 * Revisione pagamenti 2026-07-22 — helper unico "DA INCASSARE".
 *
 * Un booking checked_in con paidAmount < dovuto (currentTotalDue quando
 * disponibile, altrimenti totalAmount) e' "da incassare": il residuo va
 * mostrato con badge rosso su tile camera, pannelli dettaglio, checkout
 * modal e lista prenotazioni. Nessuna migrazione: dati gia' esposti da
 * GET /bookings (paidAmount + totalAmount + currentTotalDue).
 */
import type { BookingDto } from '../api/rooms.js';

export type PaymentStatusInput = Pick<BookingDto, 'status' | 'paidAmount' | 'totalAmount'> &
  Partial<Pick<BookingDto, 'currentTotalDue'>>;

/** Tolleranza float su importi €: sotto mezzo centesimo = pagato. */
const EPSILON = 0.005;

/**
 * Residuo da incassare in euro: dovuto − pagato, mai negativo.
 * Dovuto = currentTotalDue (base + extra live) quando disponibile,
 * altrimenti totalAmount.
 */
export function unpaidAmount(booking: PaymentStatusInput): number {
  const due = Number(booking.currentTotalDue ?? booking.totalAmount);
  const paid = Number(booking.paidAmount ?? 0);
  if (!Number.isFinite(due) || !Number.isFinite(paid)) return 0;
  return Math.max(due - paid, 0);
}

/** true se l'ospite e' in camera (checked_in) e non ha (interamente) pagato. */
export function isUnpaid(booking: PaymentStatusInput): boolean {
  return booking.status === 'checked_in' && unpaidAmount(booking) > EPSILON;
}

/** "DA INCASSARE €50.00" — label unica per badge/pill. */
export function unpaidLabel(amount: number): string {
  return `DA INCASSARE €${amount.toFixed(2)}`;
}

/**
 * ONDATA 2 P2a — caso 'pending pagato': la cascade walk-in ha incassato
 * (paid_amount > 0) ma /confirm e' fallito e il booking e' rimasto pending.
 * Il cleanup worker NON li cancella piu': la lista prenotazioni li segnala
 * con la pill arancione 'ANOMALIA PAGAMENTO' e l'operatore decide
 * (check-in del cliente reale o rimborso).
 */
export function isPendingPaidAnomaly(booking: PaymentStatusInput): boolean {
  const paid = Number(booking.paidAmount ?? 0);
  return booking.status === 'pending' && Number.isFinite(paid) && paid > EPSILON;
}

/** Label pill arancione per il pending pagato. */
export function pendingPaidAnomalyLabel(): string {
  return 'ANOMALIA PAGAMENTO';
}
